import { ActionBar, Button, Checkbox, Input, SpinnerWithBackground, TagField } from '@viapeople/components'
import { useEffect, useRef, useState } from 'react'
import { Controller, useForm, useWatch } from 'react-hook-form'
import { getLogoName } from '../../../utils/files'
import type { OrganizationFormInput } from '../types'

interface OrganizationFormProps {
	initialValues: OrganizationFormInput
	onSubmit: (data: OrganizationFormInput, logo?: File) => Promise<void>
	onCancel?: () => void
	onChange?: (data: OrganizationFormInput, hasNewLogo?: boolean) => void
	isSubmitting?: boolean
	submitButtonText?: string
	cancelButtonText?: string
	showButtons?: boolean
}

export function OrganizationForm({
	initialValues,
	onSubmit,
	onCancel,
	onChange,
	isSubmitting = false,
	submitButtonText = 'Save',
	cancelButtonText = 'Cancel',
	showButtons = true,
}: OrganizationFormProps) {
	const fileInputRef = useRef<HTMLInputElement>(null)
	const [logo, setLogo] = useState<File | undefined>(undefined)

	const {
		handleSubmit,
		control,
		setValue,
		reset,
		formState: { errors },
	} = useForm<OrganizationFormInput>({
		defaultValues: initialValues,
	})

	useEffect(() => {
		reset(initialValues)
		setLogo(undefined)
	}, [initialValues, reset])

	const formValues = useWatch({ control })
	const autoProvisionEnabled = useWatch({ control, name: 'autoProvisionOktaUsers' })

	useEffect(() => {
		if (!autoProvisionEnabled) {
			setValue('defaultOktaPassword', null)
		}
	}, [autoProvisionEnabled, setValue])

	useEffect(() => {
		if (onChange && formValues) {
			onChange(formValues as OrganizationFormInput, !!logo)
		}
	}, [formValues, logo, onChange])

	const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
		const file = event.target.files?.[0]

		if (!file?.type.startsWith('image/')) {
			return
		}
		setLogo(file)
		setValue('logo', getLogoName(file), { shouldValidate: true })
	}

	const handleUploadClick = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click()
		}
	}

	const handleCancel = () => {
		reset(initialValues)
		setLogo(undefined)
		onCancel?.()
	}

	return (
		<form onSubmit={handleSubmit((data) => onSubmit(data, logo))} className='relative pb-12 w-full'>
			<div className='grid grid-cols-1 md:grid-cols-3 gap-x6 xl:gap-x-12 gap-6 px-6 items-start'>
				<div>
					<Controller
						name='name'
						control={control}
						rules={{ required: 'Organization name is required' }}
						render={({ field }) => (
							<Input
								label='Organization Name'
								placeholder='Organization name'
								variant={errors.name ? 'error' : 'default'}
								hint={errors.name?.message}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>

				<div>
					<Controller
						name='email'
						control={control}
						rules={{
							required: 'Email is required',
							pattern: {
								value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
								message: 'Invalid email format',
							},
						}}
						render={({ field }) => (
							<Input
								label='Email'
								placeholder='Contact email'
								variant={errors.email ? 'error' : 'default'}
								hint={errors.email?.message}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>

				<div className='flex gap-4'>
					<button type='button' className='flex-1 min-w-0 cursor-pointer' onClick={handleUploadClick}>
						<Controller
							name='logo'
							control={control}
							rules={{ required: 'Logo is required' }}
							render={({ field }) => {
								return (
									<TagField
										label='Logo'
										placeholder='Upload logo'
										value={
											field.value?.length > 0
												? [{ id: '', label: getLogoName({ name: field.value } as File) }]
												: undefined
										}
										onChange={field.onChange}
										className='h-13'
										variant={errors.logo ? 'error' : 'default'}
										hint={errors.logo?.message}
									/>
								)
							}}
						/>
					</button>
					<input type='file' ref={fileInputRef} accept='image/*' onChange={handleFileUpload} className='hidden' />
					<Button
						variant='secondary'
						type='button'
						className='w-auto flex-shrink-0 h-13 self-start mt-[27px]'
						onClick={handleUploadClick}>
						Upload Logo
					</Button>
				</div>

				<div>
					<Controller
						name='contactNumber'
						control={control}
						rules={{ required: 'Contact number is required' }}
						render={({ field }) => (
							<Input
								label='Contact Number'
								placeholder='Phone number'
								type='number'
								variant={errors.contactNumber ? 'error' : 'default'}
								hint={errors.contactNumber?.message}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>

				<div>
					<Controller
						name='address'
						control={control}
						rules={{ required: 'Address is required' }}
						render={({ field }) => (
							<Input
								label='Address'
								placeholder='Street address'
								variant={errors.address ? 'error' : 'default'}
								hint={errors.address?.message}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>

				<div>
					<Controller
						name='city'
						control={control}
						rules={{ required: 'City is required' }}
						render={({ field }) => (
							<Input
								label='City'
								placeholder='City'
								variant={errors.city ? 'error' : 'default'}
								hint={errors.city?.message}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>

				<div>
					<Controller
						name='state'
						control={control}
						rules={{ required: 'State is required' }}
						render={({ field }) => (
							<Input
								label='State'
								placeholder='State'
								variant={errors.state ? 'error' : 'default'}
								hint={errors.state?.message}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>

				<div>
					<Controller
						name='zipcode'
						control={control}
						rules={{ required: 'Zip code is required' }}
						render={({ field }) => (
							<Input
								label='Zip Code'
								placeholder='Zip code'
								type='number'
								variant={errors.zipcode ? 'error' : 'default'}
								hint={errors.zipcode?.message}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>

				<div>
					<Controller
						name='country'
						control={control}
						rules={{ required: 'Country is required' }}
						render={({ field }) => (
							<Input
								label='Country'
								placeholder='Country'
								variant={errors.country ? 'error' : 'default'}
								hint={errors.country?.message}
								value={field.value}
								onChange={field.onChange}
								onBlur={field.onBlur}
							/>
						)}
					/>
				</div>
			</div>

			<div className='px-6 mt-8 mb-6'>
				<div className='border-t border-neutral-200 pt-6'>
					<div className='mb-4'>
						<h3 className='text-base font-semibold text-neutral-900'>Module Configuration</h3>
						<p className='text-sm text-neutral-600 mt-1'>Enable or disable modules for this organization</p>
					</div>
					<div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
						<div>
							<Controller
								name='autoProvisionOktaUsers'
								control={control}
								render={({ field }) => (
									<Checkbox
										checked={field.value}
										onChange={(e) => field.onChange(e.target.checked)}
										label='Auto-Provision Okta Users'
									/>
								)}
							/>
						</div>
						{autoProvisionEnabled && (
							<div className='col-span-1 md:col-span-2'>
								<Controller
									name='defaultOktaPassword'
									control={control}
									rules={{
										minLength: { value: 8, message: 'Password must be at least 8 characters' },
									}}
									render={({ field }) => (
										<Input
											label='Default Okta Password'
											placeholder='Default password for new Okta users'
											type='password'
											variant={errors.defaultOktaPassword ? 'error' : 'default'}
											hint={errors.defaultOktaPassword?.message}
											value={field.value ?? ''}
											onChange={field.onChange}
											onBlur={field.onBlur}
										/>
									)}
								/>
								<span className='text-xs text-neutral-500 mt-1 block'>
									If set, newly provisioned Okta users will receive this password and be required to change it on first
									login.
								</span>
							</div>
						)}
						<div>
							<Controller
								name='loopModuleEnabled'
								control={control}
								render={({ field }) => (
									<Checkbox
										checked={field.value}
										onChange={(e) => field.onChange(e.target.checked)}
										label='Enable Loop Module'
									/>
								)}
							/>
						</div>
						<div>
							<Controller
								name='enpsModuleEnabled'
								control={control}
								render={({ field }) => (
									<Checkbox
										checked={field.value}
										onChange={(e) => field.onChange(e.target.checked)}
										label='Enable NPS Module'
									/>
								)}
							/>
						</div>
						<div>
							<Controller
								name='pecModuleEnabled'
								control={control}
								render={({ field }) => (
									<Checkbox
										checked={field.value}
										onChange={(e) => field.onChange(e.target.checked)}
										label='Enable PEC Module'
									/>
								)}
							/>
						</div>
						<div className='flex flex-col'>
							<Controller
								name='caLimitedAccessEnabled'
								control={control}
								render={({ field }) => (
									<Checkbox
										checked={field.value}
										onChange={(e) => field.onChange(e.target.checked)}
										label='Enable CA Limited Access'
									/>
								)}
							/>
							<span className='text-xs text-neutral-500 mt-1 ml-7'>
								When enabled, Cycle Admins cannot view their own rater reviews, other CAs' data, or their manager's data
							</span>
						</div>
						<div>
							<Controller
								name='dealTeamModuleEnabled'
								control={control}
								render={({ field }) => (
									<Checkbox
										checked={field.value}
										onChange={(e) => field.onChange(e.target.checked)}
										label='Enable DealTeam Module'
									/>
								)}
							/>
						</div>
					</div>
				</div>
			</div>
			{showButtons && (
				<ActionBar
					rightActions={[
						{
							variant: 'secondary' as const,
							label: cancelButtonText,
							onClick: handleCancel,
						},
						{
							variant: 'primary' as const,
							label: submitButtonText,
							onClick: handleSubmit((data) => onSubmit(data, logo)),
							disabled: isSubmitting,
						},
					]}
				/>
			)}
			{isSubmitting && <SpinnerWithBackground />}
		</form>
	)
}
